import { useState } from "react";
import { Group, Line, Circle, Label, Tag, Text } from "react-konva";
import { fmt } from "@/lib/catalog";

import { tr } from "@/lib/i18n";
const MIN_GAP = 0.05;
const Q_LIM = [[0.05, 0.45], [0.55, 0.95]];
const clamp = (x, lo, hi) => Math.max(lo, Math.min(hi, x));
const cursor = (c) => (e) => { e.target.getStage().container().style.cursor = c; };

// limiti di un gancio: distanza dai vicini ≤ campata max, sbalzi ≤ sbalzo max del binario
function hookRange(g, idx) {
  const o = g.offsets, prev = o[idx - 1], next = o[idx + 1], last = idx === o.length - 1;
  const lo = Math.max(prev !== undefined ? prev + MIN_GAP : 0, next !== undefined ? next - g.max_span_m : -Infinity, last ? g.length - g.max_cantilever_m : -Infinity);
  const hi = Math.min(next !== undefined ? next - MIN_GAP : g.length, prev !== undefined ? prev + g.max_span_m : Infinity, idx === 0 ? g.max_cantilever_m : Infinity);
  return [lo, hi];
}

export const StructureLayer = ({ drawing, stale, busy, scale, llToWorld, prj, fields, roofs, snap, rafter, canDrag, onCommit }) => {
  const [info, setInfo] = useState(null);
  const frameOf = (fid) => fields.find((f) => f.roof.id === fid)?.frame;
  const groupOf = (x) => (drawing.groups || []).find((g) => g.fid === x.fid && g.gid === x.gid);
  const ptrUV = (e, fr) => { const w = e.target.getStage().getRelativePointerPosition(); const m = prj.worldToM(w.x, w.y); return fr.toUV(prj.toLL(m[0], m[1])); };

  const dragHook = (e, h, end) => {
    const fr = frameOf(h.fid), g = groupOf(h);
    if (!fr || !g) return;
    const vRail = fr.toUV(h.pt)[1];
    let x = ptrUV(e, fr)[0] - g.u0;
    const [lo, hi] = hookRange(g, h.idx);
    if (g.rafters?.length) {
      // solo sui travetti misurati compresi nei limiti ammessi
      const ok = g.rafters.filter((c) => c >= lo - 1e-6 && c <= hi + 1e-6);
      x = ok.length ? ok.reduce((b, c) => (Math.abs(c - x) < Math.abs(b - x) ? c : b), ok[0]) : g.offsets[h.idx];
    } else {
      if (snap && rafter > 0) x = g.offsets[0] + Math.round((x - g.offsets[0]) / rafter) * rafter;
      x = lo <= hi ? clamp(x, lo, hi) : g.offsets[h.idx];
    }
    const w = llToWorld(fr.toLL([g.u0 + x, vRail]));
    e.target.position({ x: w[0], y: w[1] });
    const o = g.offsets, prev = h.idx > 0 ? x - o[h.idx - 1] : x, next = h.idx < o.length - 1 ? o[h.idx + 1] - x : g.length - x;
    setInfo(end ? null : { w, text: `← ${fmt(prev, 2)} m · ${fmt(next, 2)} m →` });
    if (end) {
      const offs = o.map((v, j) => (j === h.idx ? Math.round(x * 1000) / 1000 : v));
      onCommit(roofs.map((r) => (r.id === h.fid ? { ...r, hook_pos: { ...(r.hook_pos || {}), [g.gid]: offs } } : r)));
    }
  };

  const dragRail = (e, rl, end) => {
    const fr = frameOf(rl.fid), g = groupOf(rl);
    if (!fr || !g) return;
    const q = Math.round(clamp((ptrUV(e, fr)[1] - g.v) / g.dv, ...Q_LIM[rl.qi]) * 100) / 100;
    const a = llToWorld(fr.toLL([g.u0, g.v + g.dv * q])), o = llToWorld(rl.line[0]);
    e.target.position({ x: a[0] - o[0], y: a[1] - o[1] });
    setInfo(end ? null : { w: a, text: tr("Binario a {0} · L modulo", [fmt(q, 2)]) });
    if (end) {
      onCommit(roofs.map((r) => {
        if (r.id !== rl.fid) return r;
        const rq = [...(r.rail_q || [0.25, 0.75])];
        rq[rl.qi] = q;
        return { ...r, rail_q: rq };
      }));
    }
  };

  return (
    <Group opacity={stale ? 0.35 : busy ? 0.6 : 1} data-testid="structure-layer">
      {(drawing.rails || []).map((rl, i) => (
        <Line key={`rl${i}-${rl.line.flat().join()}`} name={`rail-${rl.fid}-${rl.gid}-${rl.qi}`} points={rl.line.map(llToWorld).flat()} stroke={rl.ok ? "#0f172a" : "#f59e0b"}
          strokeWidth={2.2 / scale} hitStrokeWidth={10 / scale} draggable={canDrag && rl.gid !== undefined}
          onMouseEnter={canDrag ? cursor("ns-resize") : undefined} onMouseLeave={cursor("")}
          onDragMove={(e) => dragRail(e, rl, false)} onDragEnd={(e) => dragRail(e, rl, true)} />
      ))}
      {(drawing.hooks || []).map((h, i) => { const q = llToWorld(h.pt); return (
        <Circle key={`hk${i}-${q.join()}`} name={`hook-${h.fid}-${h.gid}-${h.idx}`} x={q[0]} y={q[1]} radius={3.2 / scale} hitStrokeWidth={10 / scale}
          fill={h.ok ? "#ffffff" : "#f59e0b"} stroke="#0f172a" strokeWidth={1.2 / scale} draggable={canDrag && h.gid !== undefined}
          onMouseEnter={canDrag ? cursor("ew-resize") : undefined} onMouseLeave={cursor("")}
          onDragMove={(e) => dragHook(e, h, false)} onDragEnd={(e) => dragHook(e, h, true)} />
      ); })}
      {(drawing.ballast || []).map((b, i) => { const q = llToWorld(b.pt); return (
        <Label key={`bl${i}`} x={q[0]} y={q[1]} scaleX={1 / scale} scaleY={1 / scale} offsetX={9} offsetY={7} listening={false}>
          <Tag fill="rgba(15,23,42,0.85)" cornerRadius={2} />
          <Text text={`${b.blocks}×`} fontSize={10} fill="#fff" padding={2} fontFamily="JetBrains Mono, monospace" />
        </Label>
      ); })}
      {info && (
        <Label x={info.w[0]} y={info.w[1]} scaleX={1 / scale} scaleY={1 / scale} offsetY={26} listening={false}>
          <Tag fill="#0F52BA" cornerRadius={3} pointerDirection="down" pointerWidth={8} pointerHeight={5} />
          <Text text={info.text} fontSize={11} fill="#fff" padding={4} fontFamily="JetBrains Mono, monospace" />
        </Label>
      )}
    </Group>
  );
};
